import { useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import { MANUAL_FIELDS, CHANNEL_FOR_PROVIDER } from './manual-fields.js'
import type { ChannelProviderName } from '../../../pages/admin/channels-types.js'

export interface AddAccountModalProps {
  visible: boolean
  provider: ChannelProviderName
  onCancel: () => void
  onOAuthLaunch: () => void
  onManualAdded: () => Promise<void>
}

type Mode = 'choose' | 'manual'

export default function AddAccountModal(props: AddAccountModalProps): JSX.Element {
  const toast = useToast()
  const [mode, setMode] = useState<Mode>('choose')
  const [displayName, setDisplayName] = useState('')
  const [fields, setFields] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const manualFields = MANUAL_FIELDS[props.provider] ?? [{ key: 'apiKey', label: 'API Key', type: 'password' }]

  function reset(): void {
    setMode('choose')
    setDisplayName('')
    setFields({})
  }

  function handleClose(): void {
    reset()
    props.onCancel()
  }

  function handleChooseOAuth(): void {
    props.onOAuthLaunch()
    handleClose()
  }

  async function handleManualSubmit(): Promise<void> {
    if (!displayName.trim()) {
      toast.error('Account name is required.')
      return
    }
    setSubmitting(true)
    try {
      await api.post(`/admin/channels/${props.provider}/accounts/manual`, {
        channel: CHANNEL_FOR_PROVIDER[props.provider],
        displayName: displayName.trim(),
        fields,
      })
      await props.onManualAdded()
      toast.success('Account added')
      handleClose()
    } catch (e) {
      toast.error('Failed to add account', e instanceof Error ? e.message : 'Failed to add account.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      visible={props.visible}
      header="Add Account"
      modal
      style={{ width: '30rem' }}
      onHide={handleClose}
    >
      {mode === 'choose' ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-2">
            Choose how you'd like to connect a new account.
          </p>
          <button
            onClick={handleChooseOAuth}
            className="w-full text-left rounded-lg border border-gray-200 p-4 hover:border-blue-400 hover:bg-blue-50/40 transition-colors flex items-start gap-3"
          >
            <i className="pi pi-shield text-blue-600 text-xl mt-0.5" />
            <div>
              <div className="font-semibold text-gray-900 text-sm">Connect with OAuth</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Authorize via the provider's login flow. Recommended — tokens are managed automatically.
              </div>
            </div>
          </button>
          <button
            onClick={() => setMode('manual')}
            className="w-full text-left rounded-lg border border-gray-200 p-4 hover:border-blue-400 hover:bg-blue-50/40 transition-colors flex items-start gap-3"
          >
            <i className="pi pi-key text-gray-600 text-xl mt-0.5" />
            <div>
              <div className="font-semibold text-gray-900 text-sm">Add Manually</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Enter API credentials directly (e.g. for self-hosted or non-OAuth setups).
              </div>
            </div>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Account Name</label>
            <InputText
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full"
              placeholder="e.g. Support Line"
            />
          </div>
          {manualFields.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
              <InputText
                type={f.type === 'password' ? 'password' : 'text'}
                value={fields[f.key] ?? ''}
                onChange={(e) => setFields((prev) => ({ ...prev, [f.key]: e.target.value }))}
                className="w-full"
              />
            </div>
          ))}

          <div className="flex justify-between items-center pt-2">
            <Button
              label="Back"
              onClick={() => setMode('choose')}
              disabled={submitting}
              {...({ severity: 'secondary' } as Record<string, unknown>)}
            />
            <Button
              label="Add Account"
              onClick={() => void handleManualSubmit()}
              disabled={submitting}
              loading={submitting}
            />
          </div>
        </div>
      )}
    </Dialog>
  )
}
